// hooks/useIntersectionObserver.ts
import { useCallback, useEffect, useState } from "react";

type Options = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
};

export const useIntersectionObserver = <T extends Element>({
  root = null,
  rootMargin = "0px",
  threshold = 0,
  freezeOnceVisible = false,
}: Options = {}) => {
  const [node, setNode] = useState<T | null>(null);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  const ref = useCallback((el: T | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setEntry({ isIntersecting: true } as IntersectionObserverEntry);
      return;
    }

    const observer = new IntersectionObserver(
      ([e]) => {
        setEntry(e);
        if (freezeOnceVisible && e.isIntersecting) {
          observer.disconnect();
        }
      },
      { root, rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, root, rootMargin, JSON.stringify(threshold), freezeOnceVisible]);

  return {
    ref,
    entry,
    inView: !!entry?.isIntersecting,
  };
};
